// 📄 pages/Admin/AdminPanel.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { Building2, Users, ShieldCheck, ArrowLeft, LogOut, Globe, DatabaseZap, Terminal, AlertOctagon, FileCode, ArrowLeftRight, Code2, FlaskConical, Table2, BookOpenCheck } from 'lucide-react';
import { auth } from '../../firebase';
import { useAuth } from '../../context/AuthContext.tsx';
import OrganizationsSection from './sections/OrganizationsSection.tsx';
import UsersSection from './sections/UsersSection.tsx';
import ApiCatalogSection from './sections/ApiCatalogSection.tsx';
import BadiCatalogSection from './sections/BadiCatalogSection.tsx';
import TransactionCatalogSection from './sections/TransactionCatalogSection.tsx';
import KnownErrorsCatalogSection from './sections/KnownErrorsCatalogSection.tsx';
import ValidatorTemplatesSection from './sections/ValidatorTemplatesSection.tsx';
import InterfaceCatalogSection from './sections/InterfaceCatalogSection.tsx';
import ProgramaCatalogSection from './sections/ProgramaCatalogSection.tsx';
import EscenarioPruebaCatalogSection from './sections/EscenarioPruebaCatalogSection.tsx';
import TablaCatalogSection from './sections/TablaCatalogSection.tsx';
import GuiaIntegracionSection from './sections/GuiaIntegracionSection.tsx';

type AdminSection = 'organizations' | 'users' | 'apiCatalog' | 'badiCatalog' | 'errorsCatalog' | 'validatorTemplates' | 'transactionCatalog' | 'interfaceCatalog' | 'programCatalog' | 'testScenarioCatalog' | 'tableCatalog' | 'guiaIntegracion';

export default function AdminPanel() {
  const [section, setSection] = useState<AdminSection>('organizations');
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  return (
    <div className="h-screen overflow-hidden bg-[rgb(var(--c-bg))] text-white flex">
      {/* SIDEBAR PROPIO DEL PANEL ADMIN — h-screen fijo, scroll propio solo
          en el nav si crece; el bloque inferior (Volver/Cerrar sesión)
          queda siempre visible sin importar el scroll del contenido. */}
      <aside className="w-64 h-screen flex-shrink-0 bg-white/[0.03] backdrop-blur-xl border-r border-white/10 flex flex-col p-5 gap-6">
        <div>
          <div className="flex items-center gap-2 text-indigo-400">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-widest">OneHub Admin</span>
          </div>
          <p className="text-[11px] text-gray-500 mt-1 truncate">{user?.email}</p>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-1.5">
          <button
            onClick={() => setSection('organizations')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'organizations'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <Building2 className="w-4 h-4" />
            Organizaciones
          </button>
          <button
            onClick={() => setSection('users')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'users'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <Users className="w-4 h-4" />
            Gestión de Usuarios
          </button>
          <button
            onClick={() => setSection('apiCatalog')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'apiCatalog'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <Globe className="w-4 h-4" />
            Catálogo de APIs Estándar
          </button>
          <button
            onClick={() => setSection('badiCatalog')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'badiCatalog'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <DatabaseZap className="w-4 h-4" />
            Catálogo de BAdIs
          </button>
          <button
            onClick={() => setSection('errorsCatalog')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'errorsCatalog'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <AlertOctagon className="w-4 h-4" />
            Errores Conocidos
          </button>
          <button
            onClick={() => setSection('validatorTemplates')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'validatorTemplates'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <FileCode className="w-4 h-4" />
            Plantillas del Validador
          </button>
          <button
            onClick={() => setSection('transactionCatalog')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'transactionCatalog'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <Terminal className="w-4 h-4" />
            Transacciones
          </button>
          <button
            onClick={() => setSection('interfaceCatalog')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'interfaceCatalog'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <ArrowLeftRight className="w-4 h-4" />
            Interfaces / Canales
          </button>
          <button
            onClick={() => setSection('programCatalog')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'programCatalog'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <Code2 className="w-4 h-4" />
            Programas
          </button>
          <button
            onClick={() => setSection('testScenarioCatalog')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'testScenarioCatalog'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <FlaskConical className="w-4 h-4" />
            Casos de Prueba
          </button>
          <button
            onClick={() => setSection('tableCatalog')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'tableCatalog'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <Table2 className="w-4 h-4" />
            Catálogo de Tablas
          </button>
          <button
            onClick={() => setSection('guiaIntegracion')}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${
              section === 'guiaIntegracion'
                ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 font-medium'
                : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }`}
          >
            <BookOpenCheck className="w-4 h-4" />
            Guías de Integración
          </button>
        </nav>

        <div className="flex-shrink-0 flex flex-col gap-1.5 pt-4 border-t border-white/10">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al portal
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* CONTENIDO — h-screen + overflow-y-auto propio: el scroll de las
          tarjetas queda contenido acá adentro y nunca afecta al sidebar. */}
      <main className="flex-1 h-screen overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center justify-end px-8 py-3 bg-[rgb(var(--c-bg))]/90 backdrop-blur-xl border-b border-white/10">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-white transition-all"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Volver al Portal
          </button>
        </div>
        <div className="max-w-6xl mx-auto p-8">
          {section === 'organizations' && <OrganizationsSection />}
          {section === 'users' && <UsersSection />}
          {section === 'apiCatalog' && <ApiCatalogSection />}
          {section === 'badiCatalog' && <BadiCatalogSection />}
          {section === 'errorsCatalog' && <KnownErrorsCatalogSection />}
          {section === 'validatorTemplates' && <ValidatorTemplatesSection />}
          {section === 'transactionCatalog' && <TransactionCatalogSection />}
          {section === 'interfaceCatalog' && <InterfaceCatalogSection onIrACatalogoTransacciones={() => setSection('transactionCatalog')} />}
          {section === 'programCatalog' && (
            <ProgramaCatalogSection
              onIrACatalogoBadis={() => setSection('badiCatalog')}
              onIrACatalogoTransacciones={() => setSection('transactionCatalog')}
            />
          )}
          {section === 'testScenarioCatalog' && <EscenarioPruebaCatalogSection />}
          {section === 'tableCatalog' && <TablaCatalogSection />}
          {section === 'guiaIntegracion' && <GuiaIntegracionSection />}
        </div>
      </main>
    </div>
  );
}
